import * as Sentry from '@sentry/react';
import {loadStripe, Stripe, StripeConstructor} from '@stripe/stripe-js';
import {PAYWALL_EVENTS} from '../analytics/paywallEvents';
import {getActivePaywall} from '../analytics/activePaywall';
import Analytics from './Analytics';
import Environment from './Environment';
import {isLinkOutVisit} from './linkOutSession';

/**
 * The one way a paywall loads Stripe.js. ~0.2% of checkout loads can't fetch js.stripe.com (flaky networks, content
 * blockers, corporate networks, in-app browsers), and until this loader those pages rendered no payment form at all.
 *
 * `@stripe/stripe-js` can't retry by itself: it keeps the promise of its first load in module scope, rejected or not,
 * so every later `loadStripe` call rejects with the same "Failed to load Stripe.js" without touching the network. When
 * that first load fails this loader takes over: after a short pause it removes the failed `<script>` and adds a fresh
 * one, and once that loads it builds the instance from `window.Stripe` itself. If the automatic retry fails too, the
 * failure is reported (Sentry once, `Paywall Stripe Load Failed` once) and `StripeLoadFallback` asks the person to try
 * again; `retryStripeLoad` runs another fresh load.
 *
 * Until Stripe.js loads the returned promise stays pending rather than resolving null, so a screen that is waiting
 * for its Stripe instance carries on with its normal payment UI the moment a retry succeeds, with no change to the
 * screen. It resolves null only when the publishable key is missing, which a retry can't fix.
 */

export const STRIPE_JS_URL = 'https://js.stripe.com/v3';
/** Pause before the automatic retry, so a network blip has a moment to clear. */
export const STRIPE_RETRY_DELAY_MS = 1500;

export type StripeLoadStatus = 'idle' | 'loading' | 'ready' | 'retrying' | 'failed';

type LoaderState = {
  status: StripeLoadStatus;
  /**
   * The page a checkout last asked for Stripe on; the fallback shows only there. Prefetches (a module-level promise,
   * the boot-time wallet check) don't set it, so a failed load never puts the fallback on a page that takes no payment.
   */
  path: string | null;
  /** Fresh script loads tried so far, the automatic retry included. */
  attempts: number;
};

let state: LoaderState = {status: 'idle', path: null, attempts: 0};
const listeners = new Set<() => void>();

const setState = (next: Partial<LoaderState>) => {
  state = {...state, ...next};
  listeners.forEach(listener => listener());
};

export const getStripeLoadState = (): LoaderState => state;

export const subscribeToStripeLoad = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The library's first load failed; its promise is spent, so every later call goes through the recovery below. */
let libraryFailed = false;
/** Resolves with `window.Stripe` once a fresh load succeeds. Shared by every caller waiting on the recovery. */
let recovery: Promise<StripeConstructor> | null = null;
let resolveRecovery: ((Stripe: StripeConstructor) => void) | null = null;

/** Replace any Stripe.js `<script>` with a new one: a script element that failed never loads again. */
const loadFreshScript = (): Promise<StripeConstructor> =>
  new Promise((resolve, reject) => {
    document.querySelectorAll(`script[src^="${STRIPE_JS_URL}"]`).forEach(script => script.remove());
    const script = document.createElement('script');
    script.src = STRIPE_JS_URL;
    script.addEventListener('load', () =>
      window.Stripe ? resolve(window.Stripe) : reject(new Error('Stripe.js not available')),
    );
    script.addEventListener('error', () => reject(new Error('Failed to load Stripe.js')));
    (document.head || document.body).appendChild(script);
  });

const trackLoadFailed = (recovered: boolean) =>
  Analytics.track(PAYWALL_EVENTS.stripeLoadFailed, {
    env: Environment.getVar('REACT_APP_ENV') ?? 'test',
    paywall: getActivePaywall(),
    path: window.location.pathname,
    retried: true,
    recovered,
    linkOut: isLinkOutVisit(),
  });

const attemptFreshLoad = async (): Promise<boolean> => {
  setState({status: state.status === 'idle' ? 'loading' : 'retrying', attempts: state.attempts + 1});
  try {
    const Stripe = window.Stripe ?? (await loadFreshScript());
    setState({status: 'ready'});
    resolveRecovery?.(Stripe);
    return true;
  } catch {
    setState({status: 'failed'});
    return false;
  }
};

/**
 * The automatic retry's outcome when it finished before any checkout claimed the load (a prefetch at boot, before the
 * link-out redeem has said whose visit it is). Sent when a checkout claims it, so the event names its paywall.
 */
let unreportedOutcome: {recovered: boolean} | null = null;

const startRecovery = (firstError: unknown, source: string): Promise<StripeConstructor> => {
  if (!recovery) {
    recovery = new Promise(resolve => {
      resolveRecovery = resolve;
    });
    setTimeout(async () => {
      const recovered = await attemptFreshLoad();
      if (state.path) {
        trackLoadFailed(recovered);
      } else {
        unreportedOutcome = {recovered};
      }
      if (!recovered) {
        // Only failures the person sees: a load the automatic retry rescued is in analytics, not Sentry.
        Sentry.captureException(firstError instanceof Error ? firstError : new Error(String(firstError)), {
          tags: {stripe_load: source},
        });
      }
    }, STRIPE_RETRY_DELAY_MS);
  }
  return recovery;
};

/**
 * Try Stripe.js again, from the fallback's Try again button. Resolves true when it loaded, in which case every screen
 * waiting on `loadStripeWithRetry` gets its instance.
 */
export const retryStripeLoad = (): Promise<boolean> => attemptFreshLoad();

/**
 * Record that the page on screen is a checkout waiting on Stripe, so the fallback shows here if Stripe.js can't load.
 * `loadStripeWithRetry` does this itself unless it's a prefetch; call it directly when a checkout reuses a Stripe
 * promise a prefetch started.
 */
export const markStripeNeeded = (): void => {
  if (state.path !== window.location.pathname) {
    setState({path: window.location.pathname});
  }
  if (unreportedOutcome) {
    trackLoadFailed(unreportedOutcome.recovered);
    unreportedOutcome = null;
  }
};

/**
 * Undo `markStripeNeeded` for the page on screen, when it stops needing Stripe.js (Resume going back from the new-card
 * form to the saved card, which charges without it). Leaves a claim made by another page alone.
 */
export const releaseStripeNeeded = (): void => {
  if (state.path !== null && state.path === window.location.pathname) {
    setState({path: null});
  }
};

/**
 * Load Stripe for a paywall. `source` tags the Sentry report with the screen or helper that asked. See the comment at
 * the top of this file for the retry behaviour; the promise doesn't resolve until Stripe.js has loaded. Pass
 * `prefetch` for a load started before any checkout is on screen: it loads and recovers the same way but doesn't
 * mark the current page as one that needs the fallback (see `markStripeNeeded`).
 */
export const loadStripeWithRetry = async (
  source: string,
  {prefetch = false}: {prefetch?: boolean} = {},
): Promise<Stripe | null> => {
  if (!prefetch) {
    markStripeNeeded();
  }
  const key = Environment.getVar('REACT_APP_STRIPE_KEY');
  if (!key) {
    Sentry.captureException(new Error('Stripe key must be defined'), {tags: {stripe_load: source}});
    return null;
  }
  if (!libraryFailed) {
    try {
      return await loadStripe(key);
    } catch (error) {
      libraryFailed = true;
      return (await startRecovery(error, source))(key);
    }
  }
  if (window.Stripe) {
    return window.Stripe(key);
  }
  return (await startRecovery(new Error('Failed to load Stripe.js'), source))(key);
};

/** Test-only: forget all loader state between tests. */
export const resetStripeLoaderForTests = () => {
  state = {status: 'idle', path: null, attempts: 0};
  listeners.clear();
  libraryFailed = false;
  unreportedOutcome = null;
  recovery = null;
  resolveRecovery = null;
};
